import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const PlayerDocuments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [athleteId, setAthleteId] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (athleteId) {
      fetchDocuments();
    }
  }, [athleteId]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.athlete_id) {
          setAthleteId(userData.athlete_id);
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/athlete/${athleteId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = window.document.createElement('a');
        a.href = url;
        a.download = doc.filename || 'download';
        window.document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
        toast.success('Document downloaded');
      } else {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData.detail || 'Failed to download document');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Contract': 'bg-blue-100 text-blue-800 border-blue-200',
      'Medical Record': 'bg-red-100 text-red-800 border-red-200',
      'Certificate': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'ID Document': 'bg-purple-100 text-purple-800 border-purple-200',
      'Other': 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700'
    };
    return colors[category] || colors['Other'];
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    return new Date(expirationDate) < new Date();
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Documents
          </h2>
          <p className="text-gray-600 dark:text-gray-500">View and download your documents</p>
        </div>

        {!athleteId ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-gray-500 dark:text-gray-500">Profile not linked. Contact administrator.</p>
            </CardContent>
          </Card>
        ) : documents.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-gray-500 dark:text-gray-500">No documents available</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {documents.map(doc => (
              <Card key={doc.id}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">{doc.filename}</h4>
                        <Badge className={getCategoryColor(doc.category)}>
                          {doc.category}
                        </Badge>
                        {doc.expiration_date && isExpired(doc.expiration_date) && (
                          <Badge className="bg-red-100 text-red-800 border-red-200">
                            Expired
                          </Badge>
                        )}
                        {doc.version > 1 && (
                          <Badge variant="outline">v{doc.version}</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-500">
                        <p>Uploaded: {formatDate(doc.upload_date)}</p>
                        <p>Size: {formatFileSize(doc.file_size)}</p>
                        <p>By: {doc.uploaded_by_name}</p>
                        {doc.expiration_date && (
                          <p>Expires: {formatDate(doc.expiration_date)}</p>
                        )}
                      </div>
                      {doc.notes && (
                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 italic">{doc.notes}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(doc)}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PlayerDocuments;
